<template>
  <div class="otp-verification-step card-base fade-in">
    <div class="card-body p-4">
      <h4 class="card-title mb-4 text-uppercase">
        <span class="gradient-text">Enter</span> <span class="text-primary-color">OTP</span>
      </h4>

      <form @submit.prevent="handleSubmit" class="otp-form">
        <div class="row">
          <div class="col">
            <div class="form-group mb-4">
              <div class="form-floating">
                <input
                  type="text"
                  class="form-control custom-input"
                  v-model="otp"
                  id="otpInput"
                  placeholder="Enter OTP"
                  maxlength="5"
                  required
                  autofocus
                />
                <label for="otpInput">Enter 5-digit OTP</label>
              </div>
            </div>
          </div>
        </div>

        <div class="text-center">
          <p class="error-message" v-if="errorMessage">{{ errorMessage }}</p>
          <button :disabled="loading || otp.length < 5" type="submit" class="otp-button">
            <span class="button-text">{{ loadText }}</span>
            <span v-if="loading" class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
          </button>

          <button
            type="button"
            class="resend-button"
            :disabled="resendDisabled"
            @click="resendOtp"
          >
            <span class="button-text">Resend OTP</span>
            <span v-if="resendLoading" class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
          </button>

          <p class="timer-text" v-if="resendTimer > 0">
            You can resend OTP in {{ resendTimer }} seconds.
          </p>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import CryptoJS from 'crypto-js';

export default {
  name: 'OtpVerificationStep',
  data() {
    return {
      otp: '',
      loading: false,
      errorMessage: '',
      resendLoading: false,
      resendDisabled: false,
      resendTimer: 0,
      resendCooldown: 30
    };
  },
  computed: {
    loadText() {
      return this.loading ? 'Verifying...' : 'Submit';
    }
  },
  watch: {
    otp(newVal) {
      // Auto-submit when 5 characters are entered
      if (newVal.length === 5 && !this.loading) {
        this.handleSubmit();
      }
    }
  },
  methods: {
    async handleSubmit() {
      if (this.otp.length !== 5) {
        this.errorMessage = 'Please enter a 5-digit OTP';
        return;
      }

      this.loading = true;
      this.errorMessage = '';

      try {
        const response = await axios.post(`${ticketApi}support/verifyOTP`, { otp: this.otp });

        let supportAgent = response.data.username;
        await this.$store.dispatch('recordlogin', supportAgent);

        localStorage.removeItem('users');
        localStorage.setItem('supportAgent', supportAgent);

        // Emit success to parent
        this.$emit('next', { step: 'otp-complete', data: { username: supportAgent } });
      } catch (error) {
        console.error('Error verifying OTP:', error);
        this.errorMessage =
          error.response?.data?.message || 'Failed to verify OTP. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    async resendOtp() {
      this.resendLoading = true;
      this.errorMessage = '';

      try {
        const encryptedPayload = localStorage.getItem('users');
        if (!encryptedPayload) {
          throw new Error('No user data found in storage.');
        }

        const bytes = CryptoJS.AES.decrypt(encryptedPayload, 'your-secret-key');
        const payload = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));

        await axios.post(`${ticketApi}support/requestOTP`, payload);
        this.startResendCooldown();
      } catch (error) {
        console.error('Error resending OTP:', error);
        this.errorMessage =
          error.response?.data?.message || 'Failed to resend OTP. Please try again.';
      } finally {
        this.resendLoading = false;
      }
    },

    startResendCooldown() {
      this.resendDisabled = true;
      this.resendTimer = this.resendCooldown;

      const interval = setInterval(() => {
        this.resendTimer -= 1;
        if (this.resendTimer <= 0) {
          clearInterval(interval);
          this.resendDisabled = false;
        }
      }, 1000);
    }
  }
};
</script>

<style lang="scss" scoped>
.otp-verification-step {
  padding: 0;
  width: 100%;
  background-color: var(--card-bg);
  border: var(--card-border);
  box-shadow: var(--card-shadow);
  border-radius: 16px;
  overflow: visible;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--card-shadow-hover);
  }
}

.card-body {
  padding: 2rem !important;
}

.card-title {
  font-weight: 700;
  letter-spacing: 1.2px;
  margin-bottom: 2rem;
  text-align: center;
  font-size: 1.5rem;
}

.text-primary-color {
  color: var(--text-primary);
}

.gradient-text {
  background: linear-gradient(to right, var(--accent-electric-blue), var(--accent-neon-purple), var(--accent-electric-blue));
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  opacity: 1;
}

.otp-form {
  margin-top: 0;
  opacity: 1;
  transform: translateY(0);
}

.form-group {
  position: relative;
}

.custom-input {
  border: var(--card-border);
  background-color: var(--card-bg);
  color: var(--text-primary);
  transition: all 0.3s ease;
  border-radius: 12px;
  padding: 0.75rem 1rem;
  height: 56px;
  font-size: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  text-align: center;
  letter-spacing: 0.5em;
  font-weight: 600;

  &:focus {
    box-shadow: 0 0 0 3px rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.25);
    border-color: var(--accent-electric-blue);
    outline: none;
  }

  &:hover:not(:focus) {
    border-color: var(--accent-electric-blue);
  }
}

.form-floating label {
  padding: 0.75rem 1rem;
  color: var(--text-secondary);
  font-weight: 500;
  pointer-events: none;
}

.error-message {
  color: var(--accent-danger, #ff3b30);
  font-size: 0.9rem;
  margin-bottom: 1rem;
  font-weight: 500;
}

.otp-button {
  width: 100%;
  max-width: 350px;
  height: 56px;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 1.2px;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  margin: 1.5rem auto 0;
  background: linear-gradient(to right, var(--accent-electric-blue), var(--accent-neon-purple));
  color: white;
  border: none;
  box-shadow: 0px 2px 8px rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.3);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0px 4px 16px rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg, 
      rgba(255, 255, 255, 0) 0%, 
      rgba(255, 255, 255, 0.3) 50%, 
      rgba(255, 255, 255, 0) 100%
    );
    transition: all 0.8s ease;
  }

  &:hover:not(:disabled)::before {
    left: 100%;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  .button-text {
    position: relative;
    z-index: 2;
  }

  .spinner-grow {
    margin-left: 8px;
    width: 1rem;
    height: 1rem;
  }
}

.resend-button {
  width: 100%;
  max-width: 350px;
  height: 48px;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 1px;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  margin: 1rem auto 0;
  background: var(--card-bg);
  color: var(--text-primary);
  border: 2px solid var(--card-border);
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    border-color: var(--accent-electric-blue);
    color: var(--accent-electric-blue);
    transform: translateY(-2px);
    box-shadow: 0px 4px 12px rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.2);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  .button-text {
    position: relative;
    z-index: 2;
  }

  .spinner-grow {
    margin-left: 8px;
    width: 1rem;
    height: 1rem;
  }
}

.timer-text {
  margin-top: 1rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

/* Animations removed for compatibility with reduced motion settings */

/* Responsive */
@media (max-width: 768px) {
  .card-body {
    padding: 1.5rem !important;
  }

  .card-title {
    font-size: 1.3rem;
  }

  .otp-button,
  .resend-button {
    max-width: 100%;
    font-size: 1rem;
  }
}
</style>
